import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    setLoading,
    storeHeaderActions,
    storeSearchPurchaseOrder
} from 'store/reducers/mainSlice';
import { FaPlus, FaSearch } from 'react-icons/fa';
import Filter from 'ui-component/filter-menu/Filter';
import moment from 'moment';
import { Skeleton, Tooltip } from '@mui/material';
import CurrencyValue from 'ui-component/CurrencyValue';
import toast from 'react-hot-toast';
import ExportListItemsModal from 'ui-component/ExportListItemsModal';
import PermissionAllowed from 'ui-component/permissions/PermissionAllowed';
import usePermissions from 'hooks/usePermissions';
import ActionConfirmationModal from 'ui-component/confirmation-dialogs/actionConfirmationModal/ActionConfirmationModal';
import { PiInfoFill } from 'react-icons/pi';
import DeleteConfirmation from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmation';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import {
    ViewDetailsIcon,
} from 'ui-component/custom-icons/icons';
import SortableHeader from 'ui-component/SortableHeader';
import { getCurrencySymbol, getCurrencySymbolV2, numberFormatter } from 'utils/currency';
import { FiTrash2 } from 'react-icons/fi';

import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import StatusBadge from 'ui-component/StatusBadge';
import useIsMobile from 'utils/isMobileUtils';
import ExportListItemsModalMobile from 'ui-component/ExportListItemsModalMobile';

const PurchaseOrderTabComponent = ({ id }) => {
    const history = useNavigate();
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);
    const [allPermissions, hasEveryPermission] = usePermissions();
    const [show, setShow] = useState('');
    const masterStatuses = useSelector((state) => state.main.masterStatuses);
    const statusTypes = useSelector((state) => state.main.statusTypes);
    const statusTypeList = statusTypes?.map((item) => ({ id: item.id, name: item.name }));
    const dispatch = useDispatch();

    const [searchField, setSearchField] = useState();
    const [searchValue, setSearchValue] = useState();
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [filters, setFilters] = useState([]);
    const [activeFilters, setActiveFilters] = useState({});
    const [sortByFilter, setSortByFilter] = useState();
    const headerActions = useSelector((state) => state.main.headerActions);

    const storedSearch = useSelector((state) => state.main.searchPurchaseOrder);
    const storedFilter = useSelector((state) => state.main.filterPurchaseOrder);

    const [dataList, setDataList] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableRef = useRef();
    const [showPreloadingRows, setShowPreloadingRows] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);


    const [checkedItems, setCheckedItems] = useState([]);
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);



    useEffect(() => {
        // Only run fetchData after user stops typing (debounced by searchTimeout)
        // setIsLoading(true);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            const fetchData = async () => {
                if (storedSearch) {
                    await handleSearch({ target: { value: storedSearch } });
                }
                setDataList([]);
                initializeData();
                getFiltersList();
            };
            fetchData();
        }, 500); // 500ms debounce

        setSearchTimeout(timeout);

        // Cleanup on unmount or before next effect run
        return () => clearTimeout(timeout);
        // Only trigger when these change
    }, [storedSearch, searchField, storedFilter]);

    useEffect(() => {
        setIsLoading(true);
    }, [activeFilters])
    useEffect(() => {
        const handleScroll = () => {
            if (!tableRef.current || showPreloadingRows || !hasMore) return;
            const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 10) {
                loadMoreData();
            }
        };
        const currentRef = tableRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, [showPreloadingRows, hasMore, dataList]);


    const loadMoreData = () => {
        if (showPreloadingRows || !hasMore) return;
        setShowPreloadingRows(true);
        let request = {};
        request.page = page + 1;
        request.search = searchField?.length > 2 ? searchField : null;

        api.getPurchaseInvoices(request, activeFilters, searchField, sortByFilter).then((res) => {
            // Use pagination meta from backend
            const nextPage = res?.data?.data?.meta?.current_page || request.page;
            const lastPage = res?.data?.data?.meta?.last_page || nextPage;
            if (nextPage > lastPage || res?.data?.data?.data?.length === 0) {
                setHasMore(false);
            } else {
                setDataList(prev => [...prev, ...res?.data?.data?.data || []]);
                setPage(nextPage);
            }
            setShowPreloadingRows(false);
        });
    };


    const initializeData = () => {
        // Initial load
        let request = {}
        request.page = 1;
        request.id = id;
        request.search = searchField?.length > 2 ? searchField : null;
        
        api.getPurchaseInvoicesByContact(request, activeFilters, searchField, sortByFilter).then((res) => {
            setDataList([]);
            setTimeout(() => {
                setDataList(res?.data?.data?.data || []);
                setHasMore(true);
                setPage(1);
                dispatch(setLoading(false));
                setIsLoading(false);
                setShowPreloadingRows(false);
                setTotalRecords(res?.data?.data?.total || 0);
            }, 300);
        });

    }

    const [isLoading, setIsLoading] = useState(true);

    const [confirmationDialog, setConfirmationDialog] = useState({
        show: false,
        message: '',
        title: '',
        handleConfirm: () => { },
        handleClose: () => { }
    });


    const [showQuickStats, setShowQuickStats] = useState(false);
    const [quickStatsLoading, setQuickStatsLoading] = useState(false);

    useEffect(() => {
        if (showQuickStats && !quickStatsLoading) {
            setQuickStatsLoading(true);
            api
                .quickSummarySalesOrder(
                    activeFilters,
                    searchField?.length > 2 ? searchField : null,
                    sortByFilter
                )
                .then((res) => {
                    setQuickStatsLoading(false);
                });
        }
    }, [showQuickStats]);

    const getFiltersList = () => {
        api.getPurchaseOrderFilter().then((res) => {
            setFilters(res.data);
        });
    };



    useEffect(() => {
        setShowQuickStats(false);
        dispatch(
            storeHeaderActions({
                showQuickStats: false,
                showExport: headerActions?.showExport
            })
        );
    }, [activeFilters, searchField, sortByFilter]);

    const handleClose = () => {
        setShow(null);
        setShowDeleteModal(false);
    }

    const handleDeleteDraft = (id) => {
        toast.promise(
            api.deleteDraft(id).then((res) => {
                if (res.status) {
                    setShow('');
                }

                return res?.status ? res : abc;
            }),
            {
                loading: 'Deleting Draft...',
                success: (data) => `${data.message}`,
                error: <b>Failed to delete draft</b>
            }
        );
    };

    const handleCancelEstimate = (id) => {
        toast.promise(
            api.cancelEstimation(id).then((res) => {
                if (res.status) {
                    setShow('');
                }

                return res?.status ? res : abc;
            }),
            {
                loading: 'Canceling Estimate...',
                success: (data) => `${data.message}`,
                error: <b>Failed to cancel estimate</b>
            }
        );
    };


    const [exportCompIsLoading, setExportCompIsLoading] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);


    const exportListSubmitHandler = (selectedValues) => {
        setExportCompIsLoading(true);
        api.downloadListingPurchaseOrder('', selectedValues, '', selectedCurrency).then((res) => {
            const newFileName = `Projectmix - Purchase Orders`;
            if (res != 404) {
                var newBlob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                saveAs(newBlob, `${newFileName}.xlsx`);
                // toast.dismiss(downloading);
                setExportCompIsLoading(false);
                toast.success('Downloaded Completed!');
                setShowExportModal(false);
                dispatch(
                    storeHeaderActions({
                        showExport: false
                    })
                );
            } else {
                setExportCompIsLoading(false);
                toast.error(res == 404 ? 'No Records Found!' : 'Download Failed!');
            }
        });
    };

    const handleSearch = (e) => {
        let text = e.target.value;
        
        setIsLoading(true);
        setSearchValue(text);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Only update searchField after user stops typing for 500ms
        let timeout = setTimeout(() => {
            dispatch(storeSearchPurchaseOrder(text));
            setSearchField(text)	
        }, 500);

        setSearchTimeout(timeout);

    };

    useEffect(() => {
        if (headerActions?.showQuickStats) {
            setShowQuickStats(true);
        } else {
            setShowQuickStats(false);
        }

        if (headerActions?.showExport) {
            setShowExportModal(true);
        } else {
            setShowExportModal(false);
        }
    }, [headerActions]);

    const isMobile = useIsMobile();


    const updateSortByStatus = (name) => {
        if (sortByFilter?.field == name) {
            setSortByFilter((e) => ({
                ...e,
                direction: e?.direction == 'asc' ? 'desc' : 'asc'
            }));
        } else {
            setSortByFilter((e) => ({
                field: name,
                direction: 'asc'
            }));
        }
    };
    
    useEffect(() => {
        setShowDeleteButton(checkedItems.length > 0);
    }, [checkedItems]);


    const handleDeleteMultipleRecords = () => {
        toast.promise(
            api.deleteMultipleSalesOrders({ checkedItems }).then((res) => {
                if (res.status) {
                    setIsLoading(true);
                    setDataList([]);
                    initializeData();
                    setCheckedItems([]);
                    setShowDeleteButton(false);
                    setShowDeleteModal(false);
                }

                return res?.status ? res : abc;
            }),
            {
                loading: 'Deleting Records...',
                success: (data) => `${data.message}`,
                error: <b>Failed to delete records</b>
            }
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-xl-12 col-lg-12">
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 10,
                            paddingTop: 0,
                            paddingBottom: 5,
                            paddingLeft: 20,
                            paddingRight: 20
                        }}
                        className="show-on-mobile"
                    >
                        <form className="form-inline navbar-search" style={{ flex: 1 }}>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <input
                                    type="text"
                                    className="form-control bg-white border"
                                    placeholder="Search"
                                    aria-label="Search"
                                    aria-describedby="basic-addon2"
                                    value={searchValue || ''}
                                    onChange={handleSearch}
                                    style={{ width: '100%', paddingRight: 36 }}
                                />
                                <span
                                    className="searchIcon"
                                    style={{
                                        position: 'absolute',
                                        right: 10,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#888',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <FaSearch />
                                </span>
                            </div>
                        </form>
                        <div style={{ flexShrink: 0 }}>
                            <Filter
                                activeFilters={activeFilters}
                                onSubmit={setActiveFilters}
                                filters={filters}
                                multi={undefined}
                                source={`purchase_order`}
                            />
                        </div>
                    </div>
                    <div className={`${isMobile ? 'shorter-table' : ''}`}>
                        <div className=" align-items-center justify-content-between TableSerachBar">
                            <div className="flex gap-2 align-items-center">
                                <button
                                    className="btn btn-primary btn-outlined-main-primary btn-create-report"
                                    onClick={() => {
                                        history(`/purchasing/create`);
                                    }}
                                >
                                    <FaPlus /> New
                                </button>
                                <Filter
                                    activeFilters={activeFilters}
                                    onSubmit={setActiveFilters}
                                    filters={filters}
                                    multi={undefined}
                                    source={'purchase_order'}
                                />
                            </div>
                            <form className="form-inline navbar-search" style={{ display: 'flex', gap: '5px' }} onSubmit={(e) => e.preventDefault()}>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control bg-white border"
                                        placeholder="Search"
                                        aria-label="Search"
                                        aria-describedby="basic-addon2"
                                        value={searchValue || ''}
                                        onChange={handleSearch}
                                        style={{ width: '100%' }}
                                    />
                                    <span className="searchIcon">
                                        <FaSearch />
                                    </span>
                                </div>
                            </form>
                        </div>
                        <div className="estimation-table-area">
                            <div className="table-side">

                                {
                                    !isMobile ? (
                                        <div
                                            className="table-responsive-xl tableData estimationList"
                                            ref={tableRef}
                                            style={{
                                                height: 'calc(100vh - 245px)',
                                                overflowY: 'auto',
                                                scrollbarWidth: 'thin',
                                                scrollbarColor: '#0966ff transparent'

                                            }}
                                        >
                                            <table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
                                                <thead>
                                                    <tr className="sticky-header">
                                                        
                                                        <th scope="col" width="">
                                                            <SortableHeader
                                                                name="purchase_invoice_number"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('purchase_invoice_number');
                                                                }}
                                                            >
                                                                Purchase Invoice #
                                                            </SortableHeader>
                                                        </th>

                                                        <th scope="col" width="">
                                                            <SortableHeader
                                                                name="quantity"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('quantity');
                                                                }}
                                                            >
                                                                Qty
                                                            </SortableHeader>
                                                        </th>
                                                        <th scope="col">
                                                            <SortableHeader
                                                                name="date"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('date');
                                                                }}
                                                            >
                                                                Date
                                                            </SortableHeader>
                                                        </th>
                                                        <th scope="col" width={''}>
                                                            <SortableHeader
                                                                name="status"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('status');
                                                                }}
                                                            >
                                                                Status
                                                            </SortableHeader>
                                                        </th>
                                                        <th scope="col">
                                                            <SortableHeader
                                                                name="total"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('total');
                                                                }}
                                                            >
                                                                Total
                                                            </SortableHeader>
                                                        </th>

                                                        <th scope="col">
                                                            <SortableHeader
                                                                name="open"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('open');
                                                                }}
                                                            >
                                                                Open
                                                            </SortableHeader>
                                                        </th>

                                                        <th scope="col" style={{ width: '110px' }}>
                                                            <SortableHeader
                                                                name="payment"
                                                                currentSortBy={sortByFilter}
                                                                onClick={() => {
                                                                    updateSortByStatus('payment');
                                                                }}
                                                                style={{ maxWidth: '100px' }}
                                                            >
                                                                Payment
                                                            </SortableHeader>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isLoading ? (
                                                        [...Array(100)].map((e, i) => (
                                                            <tr style={{ height: '35px' }} key={i}>
                                                                {[...Array(7)].map((e, i) => (
                                                                    <td>
                                                                        <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <>
                                                            {dataList?.length > 0 && (
                                                                dataList?.map((item, index) => {
                                                                    return (
                                                                        <tr style={{ background: `${checkedItems.includes(item.id) ? '#d9e7ff' : 'revert-layer'}` }} key={index}>
                                                                            
                                                                            <td
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {item?.master_status?.name == 'Draft' ? (
                                                                                    <span style={{ fontStyle: 'italic', color: '#929598' }}>{item?.sales_order_number}</span>
                                                                                ) : (
                                                                                    `${item?.purchase_invoice_number}`
                                                                                )}
                                                                            </td>
                                                                            <td
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {item?.quantity?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                                                            </td>
                                                                            <td
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {moment(item?.expected_delivery_date).format(CommonDateOnlyFormat)}
                                                                            </td>
                                                                            <td
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {item?.status?.name}
                                                                            </td>
                                                                            <td
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <div className="flex gap-1">
                                                                                    <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                                        {getCurrencySymbolV2(selectedCurrency, item?.currency, '11px', '11px')}
                                                                                    </div>
                                                                                    <div>
                                                                                        {item?.converted_currency_total ? numberFormatter.format(item?.converted_currency_total[selectedCurrency === 'all' ? item?.currency : selectedCurrency]) : 'N/A'}
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                
                                                                            </td>
                                                                            <td
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <div className="flex gap-1">
                                                                                    <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                                        {getCurrencySymbolV2(selectedCurrency, item?.currency, '11px', '11px')}
                                                                                    </div>
                                                                                    <div>
                                                                                        {item?.converted_currency_open ? numberFormatter.format(item?.converted_currency_open[selectedCurrency === 'all' ? item?.currency : selectedCurrency]) : 'N/A'}
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                            </td>
                                                                            <td
                                                                                style={{ width: '110px' }}
                                                                                className={
                                                                                    hasEveryPermission(['View Purchase Order'])
                                                                                        ? 'cursor-pointer align-middle font14'
                                                                                        : 'align-middle font14'
                                                                                }
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <StatusBadge itemStatus={item?.payment_status} />
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            )}
                                                            {showPreloadingRows && (
                                                                [...Array(100)].map((e, i) => (
                                                                    <tr>
                                                                        {[...Array(7)].map((e, i) => (
                                                                            <td>
                                                                                <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                ))
                                                            )}
                                                            {(dataList?.length < 1 && !isLoading) && (
                                                                <tr className="no-hover">
                                                                    <td colSpan={7}>
                                                                        <div className="not-found-container">
                                                                            <div className="not-found-icon">
                                                                                <img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} />
                                                                            </div>
                                                                            <h6 className="not-found-text">No Data Available</h6>
                                                                            <Link to={'/purchasing/create'} className="btn btn-primary btn-outlined-main">
                                                                                Create new purchase order ?
                                                                            </Link>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div
                                            className="table-responsive-xl tableData estimationList"
                                            ref={tableRef}
                                            style={{
                                                height: 'calc(100dvh - 300px)',
                                                overflowY: 'auto',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                        
                                            <div className="inv-cards-list d-md-none">
                                                    
                                                    {isLoading ? (
                                                        [...Array(7)].map((_, i) => (
                                                            <div className="picklist-mobile-card skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 5 }} key={i}>
                                                                {[...Array(7)].map((_, j) => (
                                                                    <div className="skeleton-line" key={j}></div>
                                                                ))}
                                                            </div>
                                                        ))
                                                    ) : dataList?.length > 0 ? (
                                                        dataList.map((item, index) => (
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    padding: '10px 20px',
                                                                    flexDirection: 'row',
                                                                    background: index % 2 === 0 ? '#f9f9f9' : '#fff',
                                                                    borderBottom: '1px solid #eee',
                                                                    justifyContent: 'space-between'
                                                                }}
                                                                key={item.id || index}
                                                                onClick={() => hasEveryPermission(['View Picklists (Button)']) && history(`/purchasing/view/${item?.id}`)}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 10 }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '100%' }}>
                                                                        <div className="picklist-mobile-row-13" style={{ width: '100%' }}>
                                                                            <div
                                                                                onClick={() => {
                                                                                    if (hasEveryPermission(['View Purchase Order'])) {
                                                                                        history(`/purchasing/view/${item?.id}`);
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed',
                                                                                    color: '#316aff',
                                                                                    fontWeight: '500',
                                                                                    fontSize: 15
                                                                                }}
                                                                            >
                                                                                {item?.purchase_invoice_number}
                                                                            </div>
                                                                            <div style={{ width: '90px' }}>
                                                                                <StatusBadge itemStatus={item?.payment_status} />
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            className="picklist-mobile-row-13"
                                                                            style={{
                                                                                cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed'
                                                                            }}
                                                                        >
                                                                            <div className="textGray">Orders</div>
                                                                            <div>{item?.quantity}</div>
                                                                        </div>
                                                                        <div
                                                                            className="picklist-mobile-row-13"
                                                                            style={{
                                                                                cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed'
                                                                            }}
                                                                        >
                                                                            <div className="textGray">ETA</div>
                                                                            <div>{moment(item?.expected_delivery_date).format(CommonDateOnlyFormat)}</div>
                                                                        </div>
                                                                        <div
                                                                            className="picklist-mobile-row-13"
                                                                            style={{
                                                                                cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed'
                                                                            }}
                                                                        >
                                                                            <div className="textGray">Total</div>
                                                                            <div>
                                                                                <CurrencyValue currencyObject={item?.converted_currency_total} defaultCurrency={item?.currency} />
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            className="picklist-mobile-row-13"
                                                                            style={{
                                                                                cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed'
                                                                            }}
                                                                        >
                                                                            <div className="textGray">Open</div>
                                                                            <div>
                                                                                {item?.payment_status === 'Open' ? (
                                                                                    <CurrencyValue currencyObject={item?.converted_currency_open} defaultCurrency={item?.currency} />
                                                                                ) : (
                                                                                    `${getCurrencySymbol(selectedCurrency === 'all' ? item?.currency : selectedCurrency)} 0.00`
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            className="picklist-mobile-row-13"
                                                                            style={{
                                                                                cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed'
                                                                            }}
                                                                        >
                                                                            <div className="textGray">Created</div>
                                                                            <div>{moment(item?.created_at).format(CommonDateOnlyFormat)}</div>
                                                                        </div>
                                                                        <div
                                                                            className=""
                                                                            style={{
                                                                                cursor: hasEveryPermission(['View Purchase Order']) ? 'pointer' : 'not-allowed',
                                                                                display: 'flex',
                                                                                justifyContent: 'end',
                                                                                width: '100%',
                                                                                gap: 5
                                                                            }}
                                                                        >
                                                                            <PermissionAllowed permissions={['Delete Purchase Order', 'View Purchase Order']} hasSome>
                                                                                <Tooltip title={'View'} arrow placement="top">
                                                                                    <div
                                                                                        className="btn btn-primary btn-outlined-main white-var btn-sm cursor-pointer"
                                                                                        style={{ cursor: 'pointer', marginLeft: '8px', minWidth: 'unset' }}
                                                                                        onClick={() => {
                                                                                            if (hasEveryPermission(['View Purchase Order'])) {
                                                                                                history(`/purchasing/view/${item?.id}`);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <ViewDetailsIcon height={'16'} width={'16'} />
                                                                                    </div>
                                                                                </Tooltip>
                
                                                                                <Tooltip title={'Delete'} arrow placement="top">
                                                                                    <div
                                                                                        className="btn btn-primary btn-outlined-main white-var btn-sm cursor-pointer"
                                                                                        style={{ cursor: 'pointer', marginLeft: '8px', minWidth: 'unset' }}
                                                                                        onClick={() => {
                                                                                            if (hasEveryPermission(['Delete Purchase Order'])) {
                                                                                                deleteReturnsData(item);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <FiTrash2 size={16} />
                                                                                    </div>
                                                                                </Tooltip>
                                                                            </PermissionAllowed>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="not-found-container">
                                                            <div className="not-found-icon">
                                                                <img src={NoRecordFoundIcon} style={{ height: '120px', width: '120px' }} />
                                                            </div>
                                                            <h6 className="not-found-text">No Data Available</h6>
                                                            <PermissionAllowed permissions={['Create Operation']}>
                                                                <Link to={'/operations/create'} className="btn btn-primary btn-outlined-main">
                                                                    Create Operation
                                                                </Link>
                                                            </PermissionAllowed>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    )
                                }
                                {isLoading ? (
                                    <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                        {[...Array(15)].map((_, idx) => (
                                            <div
                                                key={idx}
                                                className="order-list-card"
                                                style={{
                                                    background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                    marginBottom: 10,
                                                    borderRadius: 12,
                                                    padding: 16,
                                                    minHeight: 90,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 10
                                                }}
                                            >
                                                {[...Array(3)].map((__, i) => (
                                                    <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                        ''
                                )}
                                <div
                                    className="hide-on-mobile"
                                    style={{
                                        padding: '10px 0 0 20px',
                                        borderTop: '1px solid #e0e0e0e0',
                                        display: 'flex',
                                        gap: '5px',
                                        justifyContent: 'space-between',
                                        height: '40px',
                                    }}
                                >

                                    <div className="flex align-items-center" style={{ gap: '10px' }}>
                                        {
                                            showDeleteButton && (
                                                <>
                                                    <button 
                                                        className="btn btn-danger btn-outlined-main-danger white-va"
                                                        onClick={() => {
                                                            setShowDeleteModal(true);
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                    <div>
                                                        <span className="text-gray-800">Selected:</span>{' '}
                                                        <span className="fw-500">
                                                            {checkedItems.length ? checkedItems.length.toLocaleString('en-US') : 0}
                                                        </span>
                                                    </div>
                                                </>
                                            )
                                        }
                                    </div>
                                    <div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px' }}>
                                        <div className="text-gray-800">Total Record:</div>
                                        <div className="fw-500">
                                            {isLoading ? (
                                                <div style={{ width: '100px' }}>
                                                    <Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
                                                </div>
                                            ) : (
                                                `${totalRecords.toLocaleString('en-US')}`
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {confirmationDialog?.show && (
                <ActionConfirmationModal
                    show={confirmationDialog?.show}
                    message={confirmationDialog?.message}
                    title={confirmationDialog?.title}
                    customConfirmText={confirmationDialog?.customConfirm || 'Confirm'}
                    customCancelText={confirmationDialog?.customCancel || 'Cancel'}
                    customIcon={<PiInfoFill />}
                    handleConfirm={confirmationDialog?.handleConfirm}
                    handleClose={confirmationDialog?.handleClose}
                />
            )}

            {show && (
                <DeleteConfirmation
                    show={show}
                    handleClose={handleClose}
                    customDeleteButtonText={show?.master_status?.name == 'Estimated' ? 'Confirm' : 'Delete'}
                    handleDelete={() => {
                        if (
                            show.master_status?.name == 'Estimated' ||
                            show.master_status?.name == 'Pending Manager' ||
                            show.master_status?.name == 'Pending'
                        ) {
                            handleCancelEstimate(show?.id);
                        } else {
                            handleDeleteDraft(show?.id);
                        }
                    }}
                    title={
                        show.master_status?.name == 'Estimated' ||
                        show.master_status?.name == 'Pending Manager' ||
                        show.master_status?.name == 'Pending'
                            ? 'Confirm Cancel'
                            : 'Confirm Delete'
                    }
                    message={
                        show.master_status?.name == 'Estimated' ||
                        show.master_status?.name == 'Pending Manager' ||
                        show.master_status?.name == 'Pending'
                            ? `Are you sure you want to cancel estimate ${show.estimation_number}`
                            : `Are you sure you want to delete ${show.draft_number || show.estimation_number}`
                    }
                />
            )}

            {showExportModal && !isMobile && (
                <ExportListItemsModal
                    openpopup={true}
                    disablepopup={() => {
                        setShowExportModal(false);
                        dispatch(
                            storeHeaderActions({
                                showQuickStats: headerActions?.showQuickStats,
                                showExport: false
                            })
                        );
                    }}
                    filters={filters}
                    moduleName={'Orders'}
                    onSubmit={exportListSubmitHandler}
                    historyApi={api.getExportsHistory('orders')}
                    isLoading={exportCompIsLoading}
                    exportHistoryPerms={['Show Estimation Export History (Button)']}
                />
            )}

            {showExportModal && isMobile && (
                <ExportListItemsModalMobile
                    openpopup={true}
                    disablepopup={() => {
                        setShowExportModal(false);
                        dispatch(
                            storeHeaderActions({
                                showQuickStats: headerActions?.showQuickStats,
                                showExport: false
                            })
                        );
                    }}
                    filters={filters}
                    moduleName={'Orders'}
                    onSubmit={exportListSubmitHandler}
                    historyApi={api.getExportsHistory('orders')}
                    isLoading={exportCompIsLoading}
                    exportHistoryPerms={['Show Estimation Export History (Button)']}
                />
            )}

            {showDeleteModal && (
                <DeleteConfirmation
                    show={showDeleteModal}
                    handleClose={handleClose}
                    customDeleteButtonText={`Delete`}
                    handleDelete={handleDeleteMultipleRecords}
                    title={`Confirmation`}
                    message={`Are you sure you want to delete ${checkedItems.length} selected records from sales orders?`}
                />
            )}
        </>
    );
};

export default PurchaseOrderTabComponent;
